import React, { useState, useEffect } from 'react';
import { FiSave, FiUser, FiLock, FiBell, FiGlobe } from 'react-icons/fi';
import { UseToast } from '../../hooks/UseToast';
import Button from '../../components/ui/Button';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const Settings = () => {
  const toast = UseToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    siteName: 'Zotech',
    timezone: 'UTC+5:30'
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setProfile({
          name: data.data.name || '',
          email: data.data.email || '',
          phone: data.data.phone || '',
          company: data.data.company || ''
        });
        if (data.data.settings) {
          setSettings(data.data.settings);
        }
      } else {
        toast.error('Failed to load profile');
      }
    } catch (error) {
      toast.error('Server error');
      console.error('Fetch error:', error);
    }
    setLoading(false);
  };

  const handleProfileSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profile)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Profile updated successfully!');
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Update error:', error);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwords.current ||!passwords.new) {
      toast.error('Please fill all password fields');
      return;
    }
    if (passwords.new!== passwords.confirm) {
      toast.error('Passwords do not match!');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwords.current,
          newPassword: passwords.new
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Password changed successfully!');
        setPasswords({ current: '', new: '', confirm: '' });
      } else {
        toast.error(data.message || 'Password change failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Password error:', error);
    }
  };

  const handleToggle = (key) => {
    setSettings({...settings, [key]:!settings[key]});
  };

  const handleSettingsSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Settings saved!');
      } else {
        toast.error(data.message || 'Save failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Settings error:', error);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <FiUser /> },
    { id: 'security', label: 'Security', icon: <FiLock /> },
    { id: 'notifications', label: 'Notifications', icon: <FiBell /> },
    { id: 'general', label: 'General', icon: <FiGlobe /> }
  ];

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#64748B', fontFamily: 'Outfit, sans-serif' }}>
        Loading settings...
      </div>
    );
  }

  return (
    <>
      <style>{`
.settings-page { font-family: 'Outfit', sans-serif; }
.page-header { margin-bottom: 30px; }
.page-title { font-size: 32px; font-weight: 800; color: #0F172A; margin: 0; }
.settings-container { display: grid; grid-template-columns: 280px 1fr; gap: 30px; }
.tabs-sidebar { background: #FFF; border-radius: 16px; padding: 20px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); height: fit-content; }
.tab-item { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 10px; color: #64748B; font-weight: 600; cursor: pointer; transition: all 0.3s ease; margin-bottom: 6px; }
.tab-item:hover { background: #F1F5F9; color: #3B82F6; }
.tab-item.active { background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%); color: #FFF; }
.tab-icon { font-size: 20px; }
.settings-content { background: #FFF; border-radius: 16px; padding: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
.section-title { font-size: 24px; font-weight: 700; color: #0F172A; margin: 0 0 24px 0; }
.form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 24px; }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-label { font-size: 14px; font-weight: 600; color: #334155; }
.form-input { padding: 12px 16px; border: 2px solid #E2E8F0; border-radius: 10px; font-size: 14px; transition: all 0.3s ease; font-family: 'Outfit', sans-serif; }
.form-input:focus { outline: none; border-color: #3B82F6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
.toggle-item { display: flex; align-items: center; justify-content: space-between; padding: 16px 0; border-bottom: 1px solid #F1F5F9; }
.toggle-info h4 { font-size: 15px; font-weight: 600; color: #0F172A; margin: 0 0 4px 0; }
.toggle-info p { font-size: 13px; color: #64748B; margin: 0; }
.toggle-switch { width: 50px; height: 28px; background: #E2E8F0; border-radius: 14px; position: relative; cursor: pointer; transition: all 0.3s ease; }
.toggle-switch.active { background: #3B82F6; }
.toggle-switch::after { content: ''; position: absolute; width: 22px; height: 22px; background: #FFF; border-radius: 50%; top: 3px; left: 3px; transition: all 0.3s ease; }
.toggle-switch.active::after { left: 25px; }
        @media (max-width: 768px) {.settings-container { grid-template-columns: 1fr; }.form-grid { grid-template-columns: 1fr; } }
      `}</style>
      <div className="settings-page">
        <div className="page-header">
          <h1 className="page-title">Settings</h1>
        </div>

        <div className="settings-container">
          <div className="tabs-sidebar">
            {tabs.map(tab => (
              <div 
                key={tab.id}
                className={`tab-item ${activeTab === tab.id? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span>{tab.label}</span>
              </div>
            ))}
          </div>

          <div className="settings-content">
            {activeTab === 'profile' && (
              <>
                <h2 className="section-title">Profile Settings</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input 
                      className="form-input" 
                      value={profile.name}
                      onChange={(e) => setProfile({...profile, name: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input 
                      className="form-input" 
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({...profile, email: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input 
                      className="form-input" 
                      value={profile.phone}
                      onChange={(e) => setProfile({...profile, phone: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <input 
                      className="form-input" 
                      value={profile.company}
                      onChange={(e) => setProfile({...profile, company: e.target.value})}
                    />
                  </div>
                </div>
                <Button onClick={handleProfileSave}><FiSave /> Save Changes</Button>
              </>
            )}

            {activeTab === 'security' && (
              <>
                <h2 className="section-title">Change Password</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Current Password</label>
                    <input 
                      className="form-input" 
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input 
                      className="form-input" 
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm Password</label>
                    <input 
                      className="form-input" 
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                    />
                  </div>
                </div>
                <Button onClick={handlePasswordChange}><FiSave /> Update Password</Button>
              </>
            )}

            {activeTab === 'notifications' && (
              <>
                <h2 className="section-title">Notification Preferences</h2>
                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Email Notifications</h4>
                    <p>Receive email updates about new messages</p>
                  </div>
                  <div 
                    className={`toggle-switch ${settings.emailNotifications? 'active' : ''}`}
                    onClick={() => handleToggle('emailNotifications')}
                  ></div>
                </div>
                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Push Notifications</h4>
                    <p>Get push notifications on your device</p>
                  </div>
                  <div 
                    className={`toggle-switch ${settings.pushNotifications? 'active' : ''}`}
                    onClick={() => handleToggle('pushNotifications')}
                  ></div>
                </div>
                <Button onClick={handleSettingsSave} style={{ marginTop: '20px' }}>
                  <FiSave /> Save Preferences
                </Button>
              </>
            )}

            {activeTab === 'general' && (
              <>
                <h2 className="section-title">General Settings</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Site Name</label>
                    <input 
                      className="form-input" 
                      value={settings.siteName}
                      onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Timezone</label>
                    <select 
                      className="form-input"
                      value={settings.timezone}
                      onChange={(e) => setSettings({...settings, timezone: e.target.value})}
                    >
                      <option value="UTC-5">UTC-5 (Eastern Time)</option>
                      <option value="UTC+0">UTC+0 (GMT)</option>
                      <option value="UTC+5:30">UTC+5:30 (India)</option>
                    </select>
                  </div>
                </div>
                <Button onClick={handleSettingsSave}><FiSave /> Save Settings</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;